import { useCallback, useEffect, useReducer, useRef } from 'react';
import { identity } from 'ramda';
import uuid from 'uuid-random';

import { usePrevious } from '@core/js/hooks/usePrevious';

import { useUpdateEffect } from '@interlayer/js/hooks/useUpdateEffect';

import { actionTypes } from './store/constants';
import { createReducer } from './store/reducer';
import {
    TFetchConditionalProps,
    TFetchFN,
    TUseDataFetchWithAwaitProps,
    TUseDataFetchWithAwaitReturn
} from './types';

export const defaultParse = (response: any) => response;

export const useDataFetchWithAwait = <T, R = T, P = unknown, AP = unknown>({
    fetch,
    params,
    onResponse = defaultParse,
    onError = identity,
    initialData,
    initFetch,
    initialIsLoading = initFetch,
    shouldFetch,
    checkIsData,
    isUpdated
}: TUseDataFetchWithAwaitProps<T, R, P, AP>): TUseDataFetchWithAwaitReturn<R, P, AP> => {
    const fetchParams = useRef<unknown>(uuid());
    const prevShouldFetch = usePrevious(shouldFetch);
    const reducer = createReducer<R>();
    const [state, dispatch] = useReducer(reducer, {
        isLoading: Boolean(initialIsLoading),
        isLoaded: false,
        error: null,
        data: initialData
    });

    const resetState = useCallback(() => {
        fetchParams.current = uuid();
        dispatch({ type: actionTypes.RESET_STATE });
    }, []);

    const reFetch = useCallback(async ({
        paramsFetch,
        additionalParams,
        isUpdate
    }: TFetchConditionalProps<P, AP> = {} as TFetchConditionalProps<P, AP>): Promise<R> => {
        if (!isUpdate) {
            if (paramsFetch === fetchParams.current && state.data) {
                return Promise.resolve(state.data);
            }

            fetchParams.current = paramsFetch;

            const cacheData = checkIsData && checkIsData({ paramsFetch, additionalParams });

            if (cacheData) {
                dispatch({ type: actionTypes.FETCH_SUCCESS, payload: cacheData });

                return Promise.resolve(cacheData);
            }
        }

        if (shouldFetch && !shouldFetch({ paramsFetch, additionalParams })) {
            if (state.isLoading) {
                dispatch({ type: actionTypes.RESET_LOADING });
            }

            if (!state.data) {
                return Promise.reject('shouldFetch return false and there is no data to return');
            }

            return Promise.resolve(state.data);
        }

        dispatch({ type: actionTypes.FETCH_INIT });

        try {
            const response = await fetch(paramsFetch as P);

            const parseResponse = onResponse(response, additionalParams);

            dispatch({ type: actionTypes.FETCH_SUCCESS, payload: parseResponse });

            return parseResponse;
        } catch (err: any) {
            const legacyError = err?.response || err?.request || err;
            const error = onError(legacyError, additionalParams, reFetch as TFetchFN<R, P, AP>);

            dispatch({ type: actionTypes.FETCH_FAILURE, payload: error });

            return Promise.reject(error);
        }
    }, [checkIsData, shouldFetch, state.data, state.isLoading, fetch, onResponse, onError]);

    useEffect(() => {
        if (initFetch) {
            reFetch({ paramsFetch: params } as TFetchConditionalProps<P, AP>)
                .catch(error => console.error(error));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useUpdateEffect(() => {
        if (isUpdated && shouldFetch !== prevShouldFetch) {
            reFetch({ paramsFetch: params, isUpdate: true } as TFetchConditionalProps<P, AP>)
                .catch(error => console.error(error));
        }
    }, [isUpdated, shouldFetch, prevShouldFetch]);

    return { state, fetch: reFetch, resetState } as TUseDataFetchWithAwaitReturn<R, P, AP>;
};
